'use client';

import type { UseFormReturn } from 'react-hook-form';

import {
  getActivityEntryScopeOptions,
  getCategoryOptionsForScope,
  type ActivityScopeOptionValue,
  type ActivityEntryCategoryOption,
} from '../config/activity-entry-catalog';
import { useNavigate } from '@tanstack/react-router';

import { ReportingPeriodField } from './data-entry-form-shared';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form, FormLabel } from '@/components/ui/form';
import { Select } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';

interface PeriodOption {
  value: string;
  label: string;
}

interface ActivityFormStepOneProps {
  form: UseFormReturn;
  periodOptions: PeriodOption[];
  hasPeriods: boolean;
  isLoading: boolean;
  scopeSelection: ActivityScopeOptionValue;
  categorySelection: string;
  fixedScope: boolean;
  fixedCategory: boolean;
  error: string;
  categoryHelperText?: string | undefined;
  onScopeChange: (value: ActivityScopeOptionValue) => void;
  onCategoryChange: (value: string) => void;
  onContinue: () => void;
  onCancel: () => void;
  onPeriodChange?: (value: string) => void;
}

export function ActivityFormStepOne({
  form,
  periodOptions,
  hasPeriods,
  isLoading,
  scopeSelection,
  categorySelection,
  fixedScope,
  fixedCategory,
  error,
  categoryHelperText,
  onScopeChange,
  onCategoryChange,
  onContinue,
  onCancel,
  onPeriodChange,
}: ActivityFormStepOneProps) {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const scopeOptions = getActivityEntryScopeOptions(t);
  const categoryOptions = getCategoryOptionsForScope(scopeSelection, t);

  const selectedOption: ActivityEntryCategoryOption | undefined = categoryOptions.find(
    (opt) => opt.value === categorySelection
  );

  const handleContinue = () => {
    if (selectedOption?.redirectTo) {
      void navigate({ to: selectedOption.redirectTo });
      return;
    }
    onContinue();
  };

  return (
    <Form form={form} className="space-y-6">
      {!hasPeriods && (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>{t('activityFormStepOne.reportingPeriodRequired')}</strong> {t('activityFormStepOne.noValidPeriods')}
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border bg-muted/20 p-4" data-testid="step-one-header">
        <p className="text-sm font-medium">{t('activityFormStepOne.step1of2')}</p>
        <p className="text-sm text-muted-foreground">
          {t('activityFormStepOne.step1Description')}
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2" data-testid="step-one-scope">
        <FormLabel>{t('activityFormStepOne.label_scope')}</FormLabel>
        <Select
          options={scopeOptions}
          value={scopeSelection}
          onChange={(value) => { onScopeChange(value as ActivityScopeOptionValue); }}
          disabled={isLoading || fixedScope}
        />
        {fixedScope && (
          <p className="text-xs text-muted-foreground">{t('activityFormStepOne.scopeLocked')}</p>
        )}
      </div>

      <div className="space-y-2" data-testid="step-one-category">
        <FormLabel>{t('activityFormStepOne.label_category')}</FormLabel>
        <Select
          options={categoryOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          value={categorySelection}
          onChange={onCategoryChange}
          placeholder={t('placeholder.selectACategory')}
          disabled={isLoading || fixedCategory}
        />
        {fixedCategory && (
          <p className="text-xs text-muted-foreground">{t('activityFormStepOne.categoryLocked')}</p>
        )}
        {selectedOption?.redirectTo && (
          <p className="text-sm text-muted-foreground">
            {t('activityFormStepOne.soldProductWizardRedirect')}
          </p>
        )}
        {categoryHelperText && !selectedOption?.redirectTo && (
          <p className="text-sm text-muted-foreground">{categoryHelperText}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          data-testid="step-one-cancel"
        >
          {t('activityFormStepOne.btn_cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleContinue}
          disabled={isLoading || !hasPeriods}
          data-testid="step-one-continue"
        >
          {selectedOption?.redirectTo ? t('activityFormStepOne.btn_go_to_wizard') : t('activityFormStepOne.btn_continue')}
        </Button>
      </div>
    </Form>
  );
}
